import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FaProjectDiagram, FaSearch, FaFilter, FaEye, FaTimes } from 'react-icons/fa';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface Project {
  isDeleted: any;
  _id: string;
  projectName: string;
  description: string;
  industry: string;
  status: string;
  userId: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface ProjectsResponse {
  projects: Project[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalProjects: number;
    limit: number;
  };
}

const getStoredProjectFilters = () => {
  try {
    const saved = sessionStorage.getItem('projectsListFilters');
    if (saved) return JSON.parse(saved);
  } catch {}
  return {};
};

const ProjectsList: React.FC = () => {
  const navigate = useNavigate();
  const storedFilters = useRef(getStoredProjectFilters());
  const [searchInput, setSearchInput] = useState(storedFilters.current.searchTerm || '');
  const [searchTerm, setSearchTerm] = useState(storedFilters.current.searchTerm || '');
  const [statusFilter, setStatusFilter] = useState(storedFilters.current.statusFilter || '');
  const [projectStatusFilter, setProjectStatusFilter] = useState(storedFilters.current.projectStatusFilter || '');
  const [industryFilter, setIndustryFilter] = useState(storedFilters.current.industryFilter || '');
  const [sortBy, setSortBy] = useState(storedFilters.current.sortBy || 'createdAt');
  const [sortOrder, setSortOrder] = useState(storedFilters.current.sortOrder || 'desc');
  const [currentPage, setCurrentPage] = useState(storedFilters.current.currentPage || 1);
  const [perPage, setPerPage] = useState(storedFilters.current.perPage || 10);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const queryParams = { currentPage, perPage, searchTerm, statusFilter, projectStatusFilter, industryFilter, sortBy, sortOrder };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['projects', queryParams],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: perPage.toString(),
        sortBy,
        sortOrder,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(projectStatusFilter && { projectStatus: projectStatusFilter }),
        ...(industryFilter && { industry: industryFilter }),
      });
      const response = await api.get(`/admin/projects?${params}`);
      const d = response.data.data || response.data;
      return {
        projects: (d.projects || []) as Project[],
        totalPages: (d.pagination?.totalPages || 1) as number,
        totalProjects: (d.pagination?.totalProjects || 0) as number,
        filterCounts: d.filterCounts || {},
      };
    },
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
  });

  const projects = data?.projects || [];
  const totalPages = data?.totalPages || 1;
  const totalProjects = data?.totalProjects || 0;

  // Persist filters so they survive tab switching
  React.useEffect(() => {
    sessionStorage.setItem('projectsListFilters', JSON.stringify({
      searchTerm, statusFilter, projectStatusFilter, industryFilter, sortBy, sortOrder, currentPage, perPage
    }));
  }, [searchTerm, statusFilter, projectStatusFilter, industryFilter, sortBy, sortOrder, currentPage, perPage]);

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 500);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleStatusChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    switch (filterType) {
      case 'projectStatus':
        setProjectStatusFilter(value);
        break;
      case 'industry':
        setIndustryFilter(value);
        break;
    }
    setCurrentPage(1);
  };

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1); // Reset to first page when changing per page
  };

  const clearFilters = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchInput('');
    setSearchTerm('');
    setStatusFilter('');
    setProjectStatusFilter('');
    setIndustryFilter('');
    setSortBy('createdAt');
    setSortOrder('desc');
    setCurrentPage(1);
    setPerPage(10);
    sessionStorage.removeItem('projectsListFilters');
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) {
      return <span className="text-gray-400 text-xs ml-1">⇅</span>;
    }
    return sortOrder === 'asc' 
      ? <span className="text-primary text-xs ml-1">▲</span>
      : <span className="text-primary text-xs ml-1">▼</span>;
  };

  const SortableHeader = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {getSortIcon(field)}
      </div>
    </th>
  );

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
      published: { color: 'bg-green-100 text-green-800', label: 'Published' },
      deleted: { color: 'bg-red-100 text-red-800', label: 'Deleted' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full max-w-full overflow-hidden">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 w-full"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 truncate">Projects Management</h1>
            <p className="text-gray-600 mt-2 text-sm lg:text-base">Manage all projects in your platform</p>
          </div>
          <div className="flex items-center space-x-3 lg:space-x-4 flex-shrink-0">
            <div className="text-right">
              <p className="text-xl lg:text-2xl font-bold text-primary">{totalProjects}</p>
              <p className="text-xs lg:text-sm text-gray-500">Total Projects</p>
            </div>
            <div className="p-2 lg:p-3 bg-primary bg-opacity-10 rounded-full">
              <FaProjectDiagram className="h-6 w-6 lg:h-8 lg:w-8 text-primary" />
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col lg:flex-row gap-4">
          <form onSubmit={handleSearchSubmit} className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                placeholder="Search projects by name or description..."
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </form>

          <div className="flex items-center space-x-2">
            <FaFilter className="h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>

          {(searchInput || statusFilter) && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors flex items-center space-x-2 flex-shrink-0"
            >
              <FaTimes className="h-4 w-4" />
              <span>Clear Filters</span>
            </button>
          )}
        </div>
      </motion.div>

      {/* Projects List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl shadow-lg w-full max-w-full"
      >
        <div className="w-full relative">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center z-10">
              <LoadingSpinner size="md" />
            </div>
          )}
          <table className="w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-1/3 px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project
                </th>
                <th className="w-1/4 px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                  Owner
                </th>
                <th className="w-1/6 px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  Industry
                </th>
                <th className="w-1/6 px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="w-1/5 px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                  Created
                </th>
                <th className="w-16 px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {projects.map((project) => (
                <tr
                  key={project._id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-3 lg:px-6 py-4 w-1/3">
                    <div className="flex items-center min-w-0">
                      <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center text-white flex-shrink-0">
                        <FaProjectDiagram className="h-4 w-4 lg:h-5 lg:w-5" />
                      </div>
                      <div className="ml-2 lg:ml-4 min-w-0 flex-1">
                        <div className="text-xs lg:text-sm font-medium text-gray-900 truncate">{project.projectName}</div>
                        <div className="text-xs text-gray-500 truncate lg:block hidden">
                          {project.description}
                        </div>
                        <div className="lg:hidden block text-xs text-gray-500">
                          <span className="capitalize">{project.industry}</span>
                          <span className="mx-1">•</span>
                          <span>{project.userId.name}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 lg:px-6 py-4 w-1/4 hidden lg:table-cell">
                    <div className="flex items-center min-w-0">
                      <div className="w-8 h-8 bg-gradient-to-r from-primary to-primaryDark rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                        {getInitials(project.userId.name)}
                      </div>
                      <div className="ml-3 min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">{project.userId.name}</div>
                        <div className="text-sm text-gray-500 truncate">{project.userId.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 lg:px-6 py-4 w-1/6 hidden md:table-cell">
                    <div className="text-sm text-gray-900 capitalize truncate">{project.industry}</div>
                  </td>
                  <td className="px-3 lg:px-6 py-4 w-1/6">
                    <div className="flex justify-start">
                      {project?.isDeleted ? getStatusBadge('deleted') : getStatusBadge(project.status)}
                    </div>
                  </td>
                  <td className="px-3 lg:px-6 py-4 w-1/5 hidden sm:table-cell">
                    <div className="text-sm text-gray-500">
                      {formatDate(project.createdAt)}
                    </div>
                  </td>
                  <td className="px-3 lg:px-6 py-4 w-16">
                    <div className="flex items-center justify-center">
                      <button 
                        onClick={() => navigate(`/projects/${project._id}`)}
                        className="text-blue-600 hover:text-blue-900 transition-colors p-1"
                        title="View Details"
                      >
                        <FaEye className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-3 lg:px-6 py-3 flex items-center justify-between border-t border-gray-200 w-full">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-2 relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between min-w-0">
              <div className="flex-shrink-0 flex items-center space-x-4">
                <div>
                  <p className="text-xs lg:text-sm text-gray-700 truncate">
                    Showing <span className="font-medium">{(currentPage - 1) * perPage + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * perPage, totalProjects)}
                    </span>{' '}
                    of <span className="font-medium">{totalProjects}</span> results
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <label htmlFor="perPage" className="text-xs lg:text-sm text-gray-600">Show:</label>
                  <select
                    id="perPage"
                    value={perPage}
                    onChange={(e) => handlePerPageChange(Number(e.target.value))}
                    className="border border-gray-300 rounded-md px-2 py-1 text-xs lg:text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-xs lg:text-sm text-gray-600">per page</span>
                </div>
              </div>
              <div className="flex-shrink-0">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-xs lg:text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
{(() => {
                    const pageNumbers = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                    
                    // Adjust startPage if we're near the end
                    if (endPage - startPage < maxVisiblePages - 1) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }
                    
                    // Show first page if not in range
                    if (startPage > 1) {
                      pageNumbers.push(
                        <button
                          key={1}
                          onClick={() => setCurrentPage(1)}
                          className="relative inline-flex items-center px-2 lg:px-4 py-2 border bg-white border-gray-300 text-gray-500 hover:bg-gray-50 text-xs lg:text-sm font-medium"
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pageNumbers.push(
                          <span key="ellipsis1" className="relative inline-flex items-center px-2 lg:px-4 py-2 border border-gray-300 bg-white text-xs lg:text-sm font-medium text-gray-700">
                            ...
                          </span>
                        );
                      }
                    }
                    
                    // Show page range
                    for (let i = startPage; i <= endPage; i++) {
                      pageNumbers.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`relative inline-flex items-center px-2 lg:px-4 py-2 border text-xs lg:text-sm font-medium ${
                            currentPage === i
                              ? 'z-10 bg-primary border-primary text-white'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }
                    
                    // Show last page if not in range
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pageNumbers.push(
                          <span key="ellipsis2" className="relative inline-flex items-center px-2 lg:px-4 py-2 border border-gray-300 bg-white text-xs lg:text-sm font-medium text-gray-700">
                            ...
                          </span>
                        );
                      }
                      pageNumbers.push(
                        <button
                          key={totalPages}
                          onClick={() => setCurrentPage(totalPages)}
                          className="relative inline-flex items-center px-2 lg:px-4 py-2 border bg-white border-gray-300 text-gray-500 hover:bg-gray-50 text-xs lg:text-sm font-medium"
                        >
                          {totalPages}
                        </button>
                      );
                    }
                    
                    return pageNumbers;
                  })()}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-xs lg:text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ProjectsList; 